import axios from 'axios';

import {
  hasReminderToday,
  markReminderToday,
} from '../utils/exitIntentStorage';

export type ReminderPage = 'landing' | 'interview';
export type ReminderTrigger = 'mouse_leave' | 'idle';

export interface ScheduleReminderPayload {
  applicantGuid: string;
  page: ReminderPage;
  trigger: ReminderTrigger;
}

export interface ScheduleReminderResponse {
  status: 'success' | 'error' | 'skipped';
  data?: {
    scheduledFor?: string;
    scheduleMode?: 'test_short_delay' | 'production';
    reason?: 'already_scheduled_today';
  };
  message?: string;
}

/**
 * Fire-and-forget request to schedule a reminder email for the candidate that
 * just clicked Send Reminder / Remind me Later. The backend looks up the
 * applicant's email + job metadata from upstream APIs and hands off to
 * SendGrid; we never see the recipient on the frontend.
 *
 * Idempotency (VI-548 AC 7.2):
 *   - Same-day dedupe is enforced client-side via the `hasReminderToday` /
 *     `markReminderToday` localStorage flag. If the same applicantGuid has
 *     already requested a reminder today (in this browser), we short-circuit
 *     and return `{ status: 'skipped', reason: 'already_scheduled_today' }`
 *     without calling the backend.
 *   - This is best-effort: it does NOT cover incognito or cross-device
 *     duplicates. Strict per-applicant-per-day dedupe will require a small
 *     server-side store (a future follow-up).
 *
 * The candidate's timezone is captured here from the browser so the backend
 * can compute "next day 10am local" once we flip out of test mode.
 */
export const scheduleReminder = async ({
  applicantGuid,
  page,
  trigger,
}: ScheduleReminderPayload): Promise<ScheduleReminderResponse> => {
  if (hasReminderToday(applicantGuid)) {
    console.info(
      'Reminder already requested today for this applicant; skipping API call.',
      { applicantGuid, page, trigger },
    );
    return {
      status: 'skipped',
      data: { reason: 'already_scheduled_today' },
    };
  }

  const timezone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC';

  const response = await axios.post<ScheduleReminderResponse>(
    `/api/v1/interview/${applicantGuid}/reminder`,
    { timezone, page, trigger },
  );

  // Mark only after a successful backend handoff so a failed call (network /
  // upstream error) doesn't permanently block the candidate from retrying
  // later in the day.
  if (response.data?.status === 'success') {
    markReminderToday(applicantGuid);
  }

  return response.data;
};
